import dayjs, {
  Dayjs,
  findClosestTimeWithStep,
  formatStorage,
} from '@wox/dayjs'
import { useTranslation } from 'react-i18next'
import React, { useState, useEffect, useMemo, useCallback } from 'react'

import {
  CreateResourceReservationWithMultipleTimesDtoTimeMode,
  UpdateResourceReservationDtoAction,
} from '@/api'
import {
  KBDatePicker,
  KBFlex,
  KBForm,
  KBIcon,
  KBSpace,
  KBSwitch,
  KBText,
  KBTooltip,
  KBTypography,
} from '@/components/atoms'

import { KBDateTimeRangeSelect } from '@/components/atoms/KBDateTimeRangeSelect'
import { KBResourceReservationFormProps } from '@/components/molecules/KBResourceReservationForm/KBResourceReservationForm'
import {
  KBSlotSelector,
  type SlotSelection,
} from '@/components/molecules/KBSlotSelector'
import { getRecurrenceDescription } from '@/components/organisms/KBRecurrenceEditModal/constants'
import KBRecurrenceDropdown from '@/components/organisms/KBRecurrenceEditModal/KBRecurrenceDropdown'
import KBRecurrenceEditModal, {
  RecurrenceInfo,
} from '@/components/organisms/KBRecurrenceEditModal/KBRecurrenceEditModal'
import KBTimezoneSelect from '@/components/organisms/KBTimezoneSelect/KBTimezoneSelect'
import { ReservationTimeModeEnum, BookingModeEnum } from '@/enums/modelEnums'
import { localeAtom } from '@/store'
import { isAdminClient } from '@/utils/userEnvironment'
import { useAtom } from 'jotai'

export interface TimeSlotItem {
  startAt?: Dayjs
  endAt?: Dayjs
  timeMode?: CreateResourceReservationWithMultipleTimesDtoTimeMode
  timeZone?: string
  recurrenceInfo?: RecurrenceInfo
  multipleDates?: Dayjs[]
  // Slot booking fields
  slotSelection?: SlotSelection
}

export interface TimeSlotSelectProps {
  id?: string
  step?: number
  recurringEditType?: UpdateResourceReservationDtoAction
  value?: TimeSlotItem
  onChange?: (data?: TimeSlotItem) => void
  resourcesList: KBResourceReservationFormProps['resourcesList']
  showTimeSelect?: boolean
  allowRecurrence?: boolean
}

export const TimeSlots: React.FC<TimeSlotSelectProps> = (props) => {
  const { t } = useTranslation()
  const startAt = props.value?.startAt || dayjs()
  const {
    step = 30,
    value = {
      startAt: startAt, // findClosestTimeWithStep(startAt, step),
      endAt:
        props.value?.endAt ||
        findClosestTimeWithStep(startAt.add(step, 'minute'), step),
      timeMode: ReservationTimeModeEnum.free,
      timeZone:
        formatStorage.getTimeZone() ||
        Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    id,
    recurringEditType,
    resourcesList,
    showTimeSelect = true,
    allowRecurrence = true,
    onChange,
  } = props
  const [locale] = useAtom(localeAtom)
  const [startTimeBeforeFullDay, setStartTimeBeforeFullDay] = useState<
    Dayjs | undefined
  >()

  const TimeModesDescription = {
    [ReservationTimeModeEnum.free]: t('reservation.timeByFree'),
    [ReservationTimeModeEnum.fullDay]: t('reservation.timeByFullDay'),
    [ReservationTimeModeEnum.slot]: t('reservation.timeBySlot'),
  }

  // Determine if resource uses slot booking mode
  const bookingMode = useMemo(() => {
    if (resourcesList?.length === 1) {
      const resource = resourcesList[0]
      return resource.resource_type?.booking_mode as BookingModeEnum | undefined
    }
    return undefined
  }, [resourcesList])

  const isSlotMode = bookingMode === BookingModeEnum.slot

  const allTimeModes = [
    {
      key: ReservationTimeModeEnum.free,
      label: (
        <KBText>{TimeModesDescription[ReservationTimeModeEnum.free]}</KBText>
      ),
    },
    {
      key: ReservationTimeModeEnum.fullDay,
      label: (
        <KBText>{TimeModesDescription[ReservationTimeModeEnum.fullDay]}</KBText>
      ),
    },
  ]

  const [timeModeOptions, setTimeModeOptions] = useState(allTimeModes)

  const onTimeModeChange = (key: string) => {
    // 根据时间模式修改上面的时间选择器
    if (
      value.timeMode === ReservationTimeModeEnum.fullDay &&
      startTimeBeforeFullDay
    ) {
      value.startAt = startTimeBeforeFullDay
    }
    if (key === ReservationTimeModeEnum.fullDay) {
      // 切换全天后，保存之前选择的开始时间 - 因为全天会把开始时间初始成0点
      setStartTimeBeforeFullDay(value.startAt)
      onChange?.({
        ...value,
        timeMode: key,
        startAt: (value.startAt || dayjs()).startOf('day'),
        endAt: dayjs(value.endAt || dayjs()).endOf('day'),
      })
    } else {
      const startAt = dayjs()
      const endAt = findClosestTimeWithStep(startAt.add(1, 'hour'), step)

      onChange?.({
        ...value,
        timeMode: key as ReservationTimeModeEnum,
        startAt,
        endAt,
      })
    }
  }

  const timeZoneChange = (val: string) => {
    onChange?.({ ...value, timeZone: val })
  }

  const recurrenceDisabled = useMemo(() => {
    return (
      Boolean(recurringEditType) ||
      Number(value.multipleDates?.length) > 1 ||
      value.multipleDates?.length === 0 ||
      !allowRecurrence
    )
    // 注: 此前「全天预定且跨日」会禁用周期性, 但后端已支持周期性预定含多天全天
    // occurrence(每个 occurrence 可跨多天), 故移除该限制。
  }, [recurringEditType, value, allowRecurrence])

  const showRecurrence = useMemo(() => {
    return (
      (resourcesList || []).some((i) => {
        const rules = i.reservation_reserve_rule?.rules
        return rules?.allow_recurring
      }) || !resourcesList?.length
    )
  }, [resourcesList])

  const hideTimePicker = useMemo(() => {
    return value.timeMode !== ReservationTimeModeEnum.free
  }, [value])

  const maxAdvanceDate = useMemo(() => {
    // 管理端不做太多限制
    if (isAdminClient()) {
      return dayjs().add(10, 'year')
    }

    // 关闭提前预定-可提前预定365天，0代表不能提前预定
    let maxDate = 365
    if (resourcesList?.length) {
      const advanceRule =
        resourcesList[0].reservation_reserve_rule?.rules
          ?.reserve_start_day_limit_rule
      if (advanceRule?.is_enabled) {
        maxDate = advanceRule?.advance_limitation || 0
      }
    }
    return dayjs().add(maxDate, 'day')
  }, [resourcesList])

  const disabledDate = useCallback(
    (date: Dayjs) => {
      // 关闭提前预定-可提前预定365天，0代表不能提前预定
      const currentDate = dayjs()
      return (
        date.isBefore(currentDate, 'day') || date.isAfter(maxAdvanceDate, 'day')
      )
    },
    [maxAdvanceDate]
  )

  const startDisabledTime = useCallback(
    (date: Dayjs) => date.isBefore(dayjs(), 'minute'),
    []
  )

  const handleSlotSelectionChange = (selection: SlotSelection | undefined) => {
    if (selection) {
      const tz =
        value.timeZone || formatStorage.getTimeZone() || dayjs.tz.guess()
      // When a slot is selected, set the time mode to slot and update times
      onChange?.({
        ...value,
        timeMode: ReservationTimeModeEnum.slot,
        slotSelection: selection,
        startAt: dayjs(selection.startAt).tz(tz),
        endAt: dayjs(selection.endAt).tz(tz),
        recurrenceInfo: undefined, // Slots don't support recurrence
      })
    } else {
      onChange?.({
        ...value,
        slotSelection: undefined,
        startAt: undefined,
        endAt: undefined,
      })
    }
  }

  const renderSlotSelector = () => {
    if (resourcesList?.length !== 1) {
      return (
        <KBText type="secondary">
          {t('booking.slot.selectSingleResource')}
        </KBText>
      )
    }

    const resource = resourcesList[0]
    return (
      <KBSlotSelector
        resourceId={resource.id}
        timezone={value.timeZone}
        value={value.slotSelection}
        onChange={handleSlotSelectionChange}
      />
    )
  }

  const renderTimePicker = () => {
    // For slot mode, render the slot selector
    if (isSlotMode) {
      return renderSlotSelector()
    }

    if (value.multipleDates) {
      return (
        <KBDatePicker
          value={value.multipleDates}
          multiple
          maxTagCount={'responsive'}
          className=" tw-w-full"
          disabledDate={(date) => date.isBefore(dayjs().startOf('day'))}
          onChange={(val) => {
            let recurrenceInfo = value?.recurrenceInfo
            if (val?.length !== 1) {
              // 日期变化时清空周期性规则
              recurrenceInfo = undefined
            }
            onChange?.({
              ...value,
              multipleDates: val?.map((i: Dayjs) => dayjs(i.format())),
              recurrenceInfo,
            })
          }}
        />
      )
    }

    return (
      <KBDateTimeRangeSelect
        value={[value.startAt, value.endAt]}
        allowClear={false}
        step={step}
        hideTimePicker={hideTimePicker}
        strictlyUsingValue
        // startUseCurrentTime={value.timeMode === ReservationTimeModeEnum.free}
        // endUseCurrentTime={value.timeMode === ReservationTimeModeEnum.free}
        disabledDate={disabledDate}
        startDisabledTime={startDisabledTime}
        onChange={(start, end) => {
          let recurrenceInfo = value?.recurrenceInfo
          if (
            !start?.isSame(value.startAt, 'day') ||
            !end?.isSame(value.endAt, 'day')
          ) {
            // 日期变化时清空周期性规则
            recurrenceInfo = undefined
          }
          onChange?.({
            ...value,
            startAt: start,
            endAt: end,
            recurrenceInfo,
          })
        }}
      />
    )
  }

  // Check if we're in slot booking mode (single resource with booking_mode === 'slot')
  const isSlotBookingMode = useMemo(() => {
    if (resourcesList.length !== 1) return false
    const resource = resourcesList[0]
    // Type assertion since the API type may not include booking_mode yet
    const bookingMode = (resource.resource_type as { booking_mode?: string })
      ?.booking_mode
    return bookingMode === BookingModeEnum.slot
  }, [resourcesList])

  const handleSlotChange = (selection: SlotSelection | undefined) => {
    if (selection) {
      const tz =
        value.timeZone || formatStorage.getTimeZone() || dayjs.tz.guess()
      onChange?.({
        ...value,
        startAt: dayjs(selection.startAt).tz(tz),
        endAt: dayjs(selection.endAt).tz(tz),
        timeMode:
          ReservationTimeModeEnum.slot as CreateResourceReservationWithMultipleTimesDtoTimeMode,
        slotSelection: selection,
      })
    } else {
      onChange?.({
        ...value,
        startAt: undefined,
        endAt: undefined,
        slotSelection: undefined,
      })
    }
  }

  const reSelectTimeMode = () => {
    // Skip time mode selection for slot booking mode
    if (isSlotBookingMode) {
      setTimeModeOptions([])
      return
    }

    if (resourcesList.length === 1) {
      const [resource] = resourcesList
      const result = []
      const resourceBookingMode = resource.resource_type?.booking_mode as
        | BookingModeEnum
        | undefined

      // Handle booking mode
      if (resourceBookingMode === BookingModeEnum.slot) {
        // Slot mode - no time mode options (handled by slot selector)
        result.push({
          key: ReservationTimeModeEnum.slot,
          label: (
            <KBText>
              {TimeModesDescription[ReservationTimeModeEnum.slot]}
            </KBText>
          ),
        })
      } else if (resourceBookingMode === BookingModeEnum.fullDay) {
        // Full day mode
        result.push(allTimeModes[1])
      } else {
        // Free mode (default)
        result.push(...allTimeModes)
      }

      setTimeModeOptions(result)
    } else {
      setTimeModeOptions([...allTimeModes])
    }
  }

  useEffect(() => {
    // 隐藏不能选择的时间模式--自动定位到能选的时间模式
    reSelectTimeMode()
  }, [resourcesList, isSlotBookingMode])

  // For slot booking mode, render the slot selector instead
  if (isSlotBookingMode && resourcesList.length === 1) {
    return (
      <KBSlotSelector
        resourceId={resourcesList[0].id}
        timezone={value.timeZone}
        value={value.slotSelection}
        onChange={handleSlotChange}
      />
    )
  }

  return (
    <>
      <KBFlex id={id} align="start" vertical gap={6}>
        <KBFlex vertical gap={8} className="tw-w-full">
          {showTimeSelect && (
            <KBFlex gap={12} vertical={isSlotMode} className="tw-w-full">
              {/* 日期时间组件 */}
              {renderTimePicker()}
              {/* Hide time mode toggle for slot mode */}
              {!isSlotMode &&
                timeModeOptions.length > 1 &&
                !Boolean(value.multipleDates?.length) && (
                  <KBSpace size={8}>
                    <KBSwitch
                      checked={
                        value?.timeMode === ReservationTimeModeEnum.fullDay
                      }
                      size="small"
                      onChange={(checked) =>
                        onTimeModeChange(
                          checked
                            ? ReservationTimeModeEnum.fullDay
                            : ReservationTimeModeEnum.free
                        )
                      }
                    />

                    <KBText>{t('common.fullDay')}</KBText>
                  </KBSpace>
                )}
            </KBFlex>
          )}

          {/* Hide recurrence and timezone for slot mode */}
          {!isSlotMode && (
            <KBFlex gap={8}>
              {showRecurrence && (
                <KBRecurrenceDropdown
                  value={value.recurrenceInfo}
                  startDate={
                    value.multipleDates
                      ? value.multipleDates?.[0]?.startOf('day')
                      : value.startAt
                  }
                  reserveEndDate={
                    value.multipleDates
                      ? value.multipleDates?.[0]?.endOf('day')
                      : value.endAt
                  }
                  disabled={recurrenceDisabled}
                  maxAdvanceDate={maxAdvanceDate}
                  onChange={(info) => {
                    onChange?.({ ...value, recurrenceInfo: info })
                  }}
                />
              )}

              {value.timeMode === ReservationTimeModeEnum.free && (
                <KBForm.Item className="tw-mb-0">
                  <KBTimezoneSelect
                    className="tw-w-[191px]"
                    value={value.timeZone}
                    onChange={(value) => timeZoneChange(value)}
                    variant="borderless"
                  />
                </KBForm.Item>
              )}
            </KBFlex>
          )}
          {value.recurrenceInfo && !isSlotMode && (
            <KBText className=" tw-text-gray-500">
              {getRecurrenceDescription(value.recurrenceInfo, locale)}
            </KBText>
          )}
        </KBFlex>
      </KBFlex>
      <KBRecurrenceEditModal id="KBRecurrenceEditModal" />
    </>
  )
}

/**
 * 时间组件
 */
const TimeSlotSelect: React.FC<
  TimeSlotSelectProps & {
    errorsMessages?: string
  }
> = (props) => {
  const { t } = useTranslation()
  const { errorsMessages } = props
  const [expanded, setExpanded] = useState(false)
  return (
    <KBForm.Item
      name="timeData"
      label={
        <KBTooltip title={t('common.timeMode')}>
          <KBIcon size={24} name="clock" />
        </KBTooltip>
      }
      validateStatus={Boolean(errorsMessages?.length) ? 'error' : undefined}
      help={
        <KBTypography.Paragraph
          type="danger"
          ellipsis={{
            rows: 3,
            expandable: 'collapsible',
            expanded,
            onExpand: (_, info) => setExpanded(info.expanded),
          }}
        >
          {errorsMessages}
        </KBTypography.Paragraph>
      }
    >
      <TimeSlots {...props} />
    </KBForm.Item>
  )
}

export default React.memo(TimeSlotSelect)
